import { CatalogDataResponseItem } from "../../types";
import {
  getCatalogNumbers,
  getColorations,
  getColors,
  getName,
  getPrices,
} from "../parse-response-to-fields.util";

const parseDataForRowWithShellInPriceRemark = (dataItem: CatalogDataResponseItem) => {
  const parsedData = [];
  const catalogNumbers = getCatalogNumbers(dataItem);
  const prices = getPrices(dataItem);

  for (let catalogNumber of catalogNumbers) {
    const price = prices.find((price) => catalogNumber.remark === price.shell);
    const name = getName(dataItem, price?.shell);
    const parsedDataItem = getEssilorMagazinRawRowsData(
      [
        {
          ...dataItem,
          ...(price && { prices: [price] }),
          catalogNumbers: [catalogNumber],
          name
        }

      ]
    )

    parsedData.push(...parsedDataItem)
  }

  return parsedData;
}

const createRowsForCatalogNumbersWithAdditionalInformationName = (
  dataItem: CatalogDataResponseItem,
  currentPrices: CatalogDataResponseItem['prices'],
  currentCatalogNumber: CatalogDataResponseItem['catalogNumbers'][number],
  getName: (price: CatalogDataResponseItem['prices'][number]) => string
) =>
  currentPrices.map((price) =>
    getEssilorMagazinRawRowsData([
      {
        ...dataItem,
        ...(price && { prices: [price] }),
        catalogNumbers: [currentCatalogNumber],
        name: getName(price)
      }
    ])
  )

const parseDataForRowWithAdditionalInformationNameInCatalogNumber = (
  dataItem: CatalogDataResponseItem
) => {
  const parsedDataForRegularCatalogNumbers = [];
  const parsedDataForCatalogNumbersWithAdditionalInformation = [];
  const catalogNumbers = getCatalogNumbers(dataItem);
  const prices = getPrices(dataItem);

  for (let catalogNumber of catalogNumbers) {
    const filteredPrices = prices.filter((price) => !price.additionalInformationName);
    const parsedDataItem = createRowsForCatalogNumbersWithAdditionalInformationName(
      dataItem,
      filteredPrices,
      catalogNumber,
      (price) => getName(dataItem, `${price.shell} ${catalogNumber.remark || ''}`)
    )

    parsedDataForRegularCatalogNumbers.push(...parsedDataItem)
  }

  for (let catalogNumber of catalogNumbers) {
    const filteredPrices = prices.filter((price) => !!price.additionalInformationName);
    const parsedDataItem = createRowsForCatalogNumbersWithAdditionalInformationName(
      dataItem,
      filteredPrices,
      {
        ...catalogNumber,
        number: catalogNumber.additionalInformationNumber || catalogNumber.number,
      },
      (price) => getName(dataItem, `${price.shell} ${catalogNumber.remark || ''} ${price.additionalInformationName}`)
    )

    parsedDataForCatalogNumbersWithAdditionalInformation.push(...parsedDataItem)
  }

  return [
    ...parsedDataForRegularCatalogNumbers.flat(),
    ...parsedDataForCatalogNumbersWithAdditionalInformation.flat()
  ]
}

const parseDataForMultipleCatalogNumbers = (dataItem: CatalogDataResponseItem) => {
  const catalogNumbers = getCatalogNumbers(dataItem);
  const prices = getPrices(dataItem);
  const hasShellsInPriceSection = catalogNumbers.every((catalogNumber) => {
    const catalogNumberShell = catalogNumber.remark;
    const priceShells = prices.map((price) => price.shell);

    return catalogNumberShell && priceShells.includes(catalogNumberShell)
  });

  const hasAdditionalInformationName = catalogNumbers.some((catalogNumber) =>
    !!catalogNumber.additionalInformationName
  )

  if (hasShellsInPriceSection) {
    return parseDataForRowWithShellInPriceRemark(dataItem);
  } else if (hasAdditionalInformationName) {
    return parseDataForRowWithAdditionalInformationNameInCatalogNumber(dataItem)
  } else {
    return catalogNumbers.map((catalogNumber) => ({
      ...dataItem,
      name: getName(dataItem, catalogNumber?.remark),
      catalogNumbers: [catalogNumber]
    }))
  }
}

const parseDataFormMultiplePrices = (dataItem: CatalogDataResponseItem) => {
  const prices = getPrices(dataItem);
  const parsedData = [];

  for (let price of prices) {
    const parsedDataItem = getEssilorMagazinRawRowsData([
      {
        ...dataItem,
        name: getName(dataItem, price.shell),
        prices: [price]
      }
    ]);

    parsedData.push(...parsedDataItem)
  }

  return parsedData
}

const parseDataForMultipleColors = (dataItem: CatalogDataResponseItem, key: 'colors' | 'colorations') => {
  const colors = key === 'colors' ? getColors(dataItem) : getColorations(dataItem);
  const parsedData = [];

  for (let color of colors) {
    const parsedDataItem = getEssilorMagazinRawRowsData([
      {
        ...dataItem,
        name: getName(dataItem, `${color.name}`),
        catalogNumbers: [
          {
            ...dataItem.catalogNumbers[0],
            ...(color.number && { number: `${dataItem.catalogNumbers[0].number}${color.number}` })
          }
        ],
        colors: key === 'colors' ? [color] : [],
        colorations: key === 'colorations' ? [color] : []
      }
    ])

    parsedData.push(...parsedDataItem)
  }

  return parsedData;
}

export const getEssilorMagazinRawRowsData = (data: CatalogDataResponseItem[]) => {
  const parsedRows: CatalogDataResponseItem[] = [];

  const filteredData = data.filter((dataItem) => !!dataItem.name);

  for (let dataItem of filteredData) {
    const catalogNumbers = getCatalogNumbers(dataItem);
    const colors = getColors(dataItem);
    const colorations = getColorations(dataItem);
    const prices = getPrices(dataItem);


    if (catalogNumbers?.length > 1) {
      const rowsForCurrentCatalogNumber = parseDataForMultipleCatalogNumbers(dataItem)

      rowsForCurrentCatalogNumber.forEach((row) => parsedRows.push(row));

    } else if (prices?.length > 1) {
      const rowsForCurrentPrice = parseDataFormMultiplePrices(dataItem);

      rowsForCurrentPrice.forEach((row) => parsedRows.push(row));
    } else if (colors?.length > 1) {
      const rowsForCurrentCatalogNumber = parseDataForMultipleColors(dataItem, 'colors');

      rowsForCurrentCatalogNumber.forEach((row) => parsedRows.push(row));
    } else if (colorations?.length > 1) {
      const rowsForCurrentCatalogNumber = parseDataForMultipleColors(dataItem, 'colorations');

      rowsForCurrentCatalogNumber.forEach((row) => parsedRows.push(row));

    } else {
      parsedRows.push(dataItem)
    }
  }

  return parsedRows;
}